import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { PublicSeo } from '@/components/seo/PublicSeo';
import { PublicFooter } from '@/components/landing/PublicFooter';
import { supabase } from '@/integrations/supabase/client';

import '@/styles/landing.css';
import brandMark from '@/assets/landing/brandlogo-144.webp';
import brandWord from '@/assets/landing/brandmark-195.webp';
import heroPhoto from '@/assets/landing/hero.webp';
import heroPhoto800 from '@/assets/landing/hero-800.webp';
import heroPhoto500 from '@/assets/landing/hero-500.webp';
import ctaPhoto from '@/assets/landing/cta.webp';
import ctaPhoto800 from '@/assets/landing/cta-800.webp';
import ctaPhoto1200 from '@/assets/landing/cta-1200.webp';
import {
  Check, Users, BarChart2, ArrowRight, Target, Crosshair, Trophy,
  Activity, Calendar, PlayCircle, Award, Lock, Info, Star, Zap,
  Settings, ChevronDown, Instagram, Facebook
} from 'lucide-react';

type View = 'start' | 'training' | 'liga' | 'preise';

const APP_LOGIN = '/startseite';
const APP_REGISTER = '/startseite?auth=register';

const TRUSTPILOT_URL = 'https://de.trustpilot.com/review/aimupdarts.com';

interface Review {
  id: string;
  title: string;
  body: string;
  author_name: string;
  stars: number;
  country: string | null;
}

const ReviewStars = ({ value }: { value: number }) => (
  <div className="rv-stars" aria-label={`${value} von 5 Sternen`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={18} className={i <= value ? 'on' : 'off'} fill={i <= value ? 'currentColor' : 'none'} strokeWidth={1.6} />
    ))}
  </div>
);

const ReviewsSection = () => {
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(3);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('reviews')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      if (active) setReviews((data as Review[]) ?? []);
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const calc = () => {
      const w = window.innerWidth;
      setPerView(w < 720 ? 1 : w < 1024 ? 2 : 3);
    };
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, []);

  const total = reviews?.length ?? 0;
  const maxIndex = Math.max(0, total - perView);
  const startXRef = useRef<number | null>(null);

  useEffect(() => { setIndex((i) => Math.min(i, maxIndex)); }, [maxIndex]);

  if (!reviews || total === 0) return null;

  const showNav = total > perView;

  const SWIPE_THRESHOLD = 50;

  const prev = () => setIndex((i) => Math.max(0, i - 1));
  const next = () => setIndex((i) => Math.min(maxIndex, i + 1));

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    startXRef.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    const start = startXRef.current;
    if (start === null) return;
    const delta = start - e.changedTouches[0].clientX;
    if (delta > SWIPE_THRESHOLD) {
      next();
    } else if (delta < -SWIPE_THRESHOLD) {
      prev();
    }
    startXRef.current = null;
  };

  return (
    <section className="section">
      <div className="wrap">
        <div className="section-head center reveal in">
          <span className="eyebrow"><span className="dot"></span>Echte Bewertungen</span>
          <h2>Das sagen unsere Spieler</h2>
          <p className="lead">Ehrliches Feedback von echten Nutzern – nachzulesen auf Trustpilot.</p>
        </div>

        <div className="rv-slider reveal in">
          <div className="rv-viewport" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            <div
              className="rv-track"
              style={{ transform: `translateX(-${index * (100 / perView)}%)` }}
            >
              {reviews.map((r) => (
                <div className="rv-slide" key={r.id} style={{ flex: `0 0 ${100 / perView}%` }}>
                <article className="rv-card">
                  <ReviewStars value={r.stars} />
                  <h3 className="rv-title">{r.title}</h3>
                  <p className="rv-body">{r.body}</p>
                  <div className="rv-foot">
                    <span className="rv-avatar">{(r.author_name || '?').charAt(0).toUpperCase()}</span>
                    <div>
                      <div className="rv-name">{r.author_name}</div>
                      {r.country && <div className="rv-country">{r.country}</div>}
                    </div>
                  </div>
                </article>
                </div>
              ))}
            </div>
          </div>
        </div>

        {showNav && (
          <div className="rv-dots">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`rv-dot ${i === index ? 'active' : ''}`}
                onClick={() => setIndex(i)}
                aria-label={`Zu Bewertung ${i + 1}`}
              />
            ))}
          </div>
        )}

        <div className="center">
          <a className="rv-link" href={TRUSTPILOT_URL} target="_blank" rel="noopener noreferrer">
            Alle Bewertungen auf Trustpilot ansehen →
          </a>
        </div>
      </div>
    </section>
  );
};


const Landing = () => {
  const { language } = useLanguage();
  const [view, setView] = useState<View>('start');
  const [menuOpen, setMenuOpen] = useState(false);

  // Hash-based view switching, matches original page behaviour.
  useEffect(() => {
    const fromHash = () => {
      const h = (window.location.hash || '').replace('#', '');
      if (h === 'training' || h === 'liga' || h === 'start' || h === 'preise') setView(h as View);
      else setView('start');
      window.scrollTo({ top: 0 });
    };
    fromHash();
    window.addEventListener('hashchange', fromHash);
    return () => window.removeEventListener('hashchange', fromHash);
  }, []);

  // Close mobile menu on resize.
  useEffect(() => {
    const onResize = () => { if (window.innerWidth > 880) setMenuOpen(false); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const navItem = (id: View, label: string) => (
    <a
      key={id}
      className={`nav-link ${view === id ? 'active' : ''}`}
      href={`#${id}`}
      onClick={() => setMenuOpen(false)}
    >
      {label}
    </a>
  );

  return (
    <div className="lp-root">
      <PublicSeo
        slug=""
        titleDe="Dart Training App & persönlicher Dart-Coach | AimUp"
        titleEn="AimUp: Your personal darts coach"
        descriptionDe="AimUp ist die Dart Training App mit persönlichem Trainingsplan, Scoring-, Doppel- und Checkout-Übungen und Statistiken. Kostenlos im Browser starten."
        descriptionEn="AimUp is your personal darts coach: structured training, honest stats, awards and the AimUp League, right in your browser."
      />

      {/* ─────────────────── NAV ─────────────────── */}
      <header className={`nav ${menuOpen ? 'open' : ''}`} id="siteNav">
        <div className="wrap nav-inner">
          <a className="brand" href="#start" aria-label="AimUp Startseite" onClick={() => setMenuOpen(false)}>
            <img className="brand-mark" src={brandMark} alt="" width={144} height={126} decoding="async" />
            <img className="brand-word" src={brandWord} alt="AimUp" width={195} height={63} decoding="async" />
          </a>
          <nav className="nav-links" aria-label="Hauptnavigation">
            {navItem('start', 'Startseite')}
            {navItem('training', 'Persönliches Training')}
            {navItem('liga', 'Liga')}
            {navItem('preise', 'Preise')}
          </nav>
          <div className="nav-right">
            <Link className="btn btn-ghost btn-sm nav-auth" to={APP_LOGIN}>Login</Link>
            <Link className="btn btn-primary btn-sm nav-auth" to={APP_REGISTER}>Registrieren</Link>
            <Link className="btn btn-ghost btn-sm nav-login-mobile" to={APP_LOGIN}>Login</Link>
            <button
              className="burger"
              type="button"
              aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
              aria-expanded={menuOpen}
              aria-controls="mobileMenu"
              onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
            >
              <span></span><span></span><span></span>
            </button>
          </div>
        </div>
        <div className="mobile-menu" id="mobileMenu">
          <nav className="mm-links" aria-label="Navigation">
            {navItem('start', 'Startseite')}
            {navItem('training', 'Persönliches Training')}
            {navItem('liga', 'Liga')}
            {navItem('preise', 'Preise')}
          </nav>
          <div className="mm-auth">
            <Link className="btn btn-ghost" to={APP_LOGIN}>Login</Link>
            <Link className="btn btn-primary" to={APP_REGISTER}>Registrieren</Link>
          </div>
        </div>
      </header>

      <main id="top">
        {/* ═══════════════ START ═══════════════ */}
        <div className="view" data-view="start" hidden={view !== 'start'}>
          <section className="hero">
            <div className="wrap">
              <span className="eyebrow hero-eyebrow"><span className="dot"></span>Dein persönlicher Dart-Coach</span>
              <div className="hero-grid">
                <div className="hero-copy">
                  <h1>Dein bestes Spiel<br /><span className="hl">beginnt hier.</span></h1>
                  <p className="lead">
                    AimUp ist deine Dart Training App mit persönlichem Trainingsplan, ehrlichen Statistiken und der AimUp&nbsp;Liga.
                    So bleibst du dran, vom ersten Wurf bis aufs Turnier-Level.
                  </p>
                  <div className="hero-cta">
                    <Link className="btn btn-primary" to={APP_REGISTER}>Jetzt kostenlos trainieren</Link>
                    <a className="btn btn-ghost" href="#training" onClick={() => setView('training')}>So funktioniert's</a>
                  </div>
                  <div className="hero-meta">
                    <div className="stat"><div className="v num">30<span className="accent">+</span></div><div className="l">Spielmodi</div></div>
                    <div className="stat"><div className="v num">7</div><div className="l">Ligastufen</div></div>
                    <div className="stat"><div className="v num"><span className="inf">∞</span></div><div className="l">Sessions</div></div>
                  </div>
                </div>
                <div className="hero-media">
                  <img
                    className="hero-photo"
                    src={heroPhoto}
                    srcSet={`${heroPhoto500} 500w, ${heroPhoto800} 800w, ${heroPhoto} 1600w`}
                    sizes="(max-width: 900px) 100vw, 50vw"
                    width={1600}
                    height={1313}
                    alt="Dartspieler beim Wurf auf eine Winmau-Dartscheibe"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />

                </div>
              </div>
            </div>
          </section>

          <section className="strip">
            <div className="wrap strip-row">
              <div className="strip-item"><Check size={18} strokeWidth={2.4} />Kein Download, läuft im Browser</div>
              <div className="strip-item"><Check size={18} strokeWidth={2.4} />Ideal für Steel- und Softdarts</div>
              <div className="strip-item"><Check size={18} strokeWidth={2.4} />Anfänger bis Turnierspieler</div>
            </div>
          </section>

          <ReviewsSection />


          <section className="section soft">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>So funktioniert's</span>
                <h2>Was AimUp für dich macht</h2>
                <p className="lead">Drei Bausteine, ein Ziel: dass du Woche für Woche besser wirst, ganz ohne Rätselraten.</p>
              </div>
              <div className="modes-grid m3 reveal in">
                <a className="mode" href="#training" onClick={() => setView('training')}>
                  <div className="mi"><Target size={22} /></div>
                  <h3>Strukturiertes Training</h3>
                  <p>Über 30 Spielmodi und ein Coach, der dir jeden Tag sagt, was dran ist. Vom Scoring bis zum Checkout.</p>
                </a>
                <a className="mode" href="#training" onClick={() => setView('training')}>
                  <div className="mi"><BarChart2 size={22} /></div>
                  <h3>Ehrliche Statistiken</h3>
                  <p>Jeder Wurf wird getrackt. Average, Trefferquote und Checkouts, dein Fortschritt schwarz auf weiß.</p>
                </a>
                <a className="mode" href="#liga" onClick={() => setView('liga')}>
                  <div className="mi"><Trophy size={22} /></div>
                  <h3>AimUp Liga</h3>
                  <p>Miss dich Woche für Woche mit anderen, sammle XP und klettere durch die Ligastufen. Ganz ohne Verein.</p>
                </a>
              </div>
              <div className="center reveal in" style={{ marginTop: 'clamp(28px,4vw,40px)' }}>
                <Link className="btn btn-primary" to={APP_REGISTER}>Jetzt kostenlos trainieren</Link>
              </div>
            </div>
          </section>
        </div>

        {/* ═══════════════ TRAINING ═══════════════ */}
        <div className="view" data-view="training" hidden={view !== 'training'}>
          <section className="section">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>Persönliches Training</span>
                <h2>Dein geführtes Coaching-System</h2>
                <p className="lead">
                  Kein planloses Werfen: Ein Performance-Test misst dein echtes Niveau, daraus entsteht ein adaptiver Wochenplan,
                  durch den dich ein Live-Coach Einheit für Einheit begleitet. Am Planende passt sich dein Level an deine Form an.
                </p>
              </div>
            </div>
          </section>

          <section className="section soft">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>Der Trainingskreislauf</span>
                <h2>Vier Schritte, eine Schleife</h2>
                <p className="lead">Test, Plan, Coach-Session und Auswertung greifen ineinander, vom ersten Test bis zur fortlaufenden Anpassung an deine Form.</p>
              </div>
              <div className="loop reveal in">
                {[
                  { n: 'Schritt 1', ic: <Activity size={22} />, h: 'Performance-Test', p: 'Aufwärmen, Scoring, Doppel und Checkout werfen. Ergibt dein Level von 1 bis 10 und deinen persönlichen Fokus.' },
                  { n: 'Schritt 2', ic: <Calendar size={22} />, h: 'Trainingsplan', p: 'Du wählst Tage, Dauer und Wochen. Den Fokus zieht die App automatisch aus deinem Test.' },
                  { n: 'Schritt 3', ic: <PlayCircle size={22} />, h: 'Coach-Session', p: 'Die tägliche, live begleitete Einheit entlang deines Plans. Jedes Ergebnis wird gespeichert.' },
                  { n: 'Schritt 4', ic: <Award size={22} />, h: 'Auswertung', p: 'Am Planende wird dein Level je nach Leistung um ±1 angepasst. Dann startet die Schleife neu.' },
                ].map((s, i) => (
                  <div className="step" key={i}>
                    <div className="sn">{s.n}</div>
                    <div className="si">{s.ic}</div>
                    <h3>{s.h}</h3>
                    <p>{s.p}</p>
                  </div>
                ))}
              </div>
              <div className="coach-note reveal in" style={{ maxWidth: 760, marginInline: 'auto', marginTop: 20 }}>
                <Info size={20} />
                <span>Wichtig: Dein Level ändert sich <b>nur am Ende eines kompletten Plans</b>, nicht nach jeder Session. Das schützt deinen Fortschritt vor einzelnen schwachen Tagen.</span>
              </div>
            </div>
          </section>

          {/* Schritt 1 · Performance-Test */}
          <section className="section">
            <div className="wrap">
              <div className="feature reveal in">
                <div className="feature-copy">
                  <span className="eyebrow"><span className="dot"></span>Schritt 1 · Performance-Test</span>
                  <h2>Erst messen,<br />dann trainieren.</h2>
                  <p className="lead">Ein einmaliger Durchlauf von 12 bis 15 Minuten: Aufwärmen plus drei bewertete Disziplinen. Nach jedem Pfeil tippst du das Ergebnis ein, so wie im echten Spiel. Daraus berechnet AimUp dein Level und erkennt, woran du arbeiten solltest.</p>
                  <ul className="feature-points">
                    <li>30 Pfeile Scoring auf die Triple 20</li>
                    <li>36 Pfeile auf sechs verschiedene Doppel</li>
                    <li>8 Checkout-Finishes mit empfohlener Route</li>
                    <li>Ergebnis: Level 1 bis 10, Stärken, Schwächen &amp; Fokus</li>
                  </ul>
                </div>
                <div className="feature-media">
                  <div className="mock">
                    <div className="mock-head">
                      <div><div className="t">Performance-Test · Beispiel</div><div className="h">Dein Ergebnis</div></div>
                      <div className="mock-ic"><Activity size={18} /></div>
                    </div>
                    <div className="stat-cards">
                      <div className="c"><div className="l">Scoring · T20</div><div className="v num">52%</div><div className="d">Stärke</div></div>
                      <div className="c"><div className="l">Doppel</div><div className="v num">28%</div><div className="d" style={{ color: '#C8870F' }}>Fokus</div></div>
                      <div className="c"><div className="l">Checkout</div><div className="v num">41%</div></div>
                    </div>
                    <div className="tres">
                      <div className="lv">5</div>
                      <div>
                        <div className="nm">Level 5 · Vereinsspieler</div>
                        <div className="ds">Hauptfokus: Doppel · Drift: leicht links</div>
                      </div>
                    </div>
                    <div className="tweight"><span>Gewichtung fürs Level</span><span>Scoring 40% · Doppel 35% · Checkout 25%</span></div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Schritt 2 · Trainingsplan */}
          <section className="section soft">
            <div className="wrap">
              <div className="feature reverse reveal in">
                <div className="feature-copy">
                  <span className="eyebrow"><span className="dot"></span>Schritt 2 · Trainingsplan</span>
                  <h2>Du wählst drei Dinge.<br />Den Rest plant AimUp.</h2>
                  <p className="lead">Lege Trainingstage, Dauer und Zeitraum fest, den Rest verteilt die App. Scoring, Doppel und Checkout landen sinnvoll über die Wochen verteilt. Dein Hauptfokus kommt aus dem Test und bleibt gesetzt, damit aus deiner Schwäche eine Stärke wird.</p>
                  <ul className="feature-points">
                    <li>Trainingstage frei wählbar (Standard Mo, Mi, Fr)</li>
                    <li>20, 40 oder 60 Minuten pro Einheit</li>
                    <li>Zeitraum 1 bis 4 Wochen, Intensität steigt leicht an</li>
                  </ul>
                </div>
                <div className="feature-media">
                  <div className="mock">
                    <div className="mock-head">
                      <div><div className="t">Plan erstellen · Beispiel</div><div className="h">Dein Setup</div></div>
                      <div className="mock-ic"><Settings size={18} /></div>
                    </div>
                    <div className="setup">
                      <div>
                        <div className="sl">1 · Trainingstage</div>
                        <div className="chips">{['Mo','Di','Mi','Do','Fr','Sa','So'].map(d => (
                          <span key={d} className={`chip ${['Mo','Mi','Fr'].includes(d) ? 'on' : ''}`}>{d}</span>
                        ))}</div>
                      </div>
                      <div>
                        <div className="sl">2 · Dauer pro Einheit</div>
                        <div className="chips">
                          <span className="chip">20 Min</span><span className="chip on">40 Min</span><span className="chip">60 Min</span>
                        </div>
                      </div>
                      <div>
                        <div className="sl">3 · Zeitraum</div>
                        <div className="chips">
                          <span className="chip">1 Wo</span><span className="chip">2 Wo</span><span className="chip">3 Wo</span><span className="chip on">4 Wo</span>
                        </div>
                      </div>
                      <div className="lock"><Lock size={16} /><span><b>Fokus: Doppel</b>, automatisch aus deinem Test, im Plan gesperrt.</span></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Schritt 3 · Live-Coach-Session */}
          <section className="section">
            <div className="wrap">
              <div className="feature reveal in">
                <div className="feature-copy">
                  <span className="eyebrow"><span className="dot"></span>Schritt 3 · Live-Coach-Session</span>
                  <h2>Jeden Tag weißt du,<br />was dran ist.</h2>
                  <p className="lead">Die Coach-Session ist deine eigentliche Trainingseinheit. Sie lädt automatisch die passende Übung aus deinem Plan, mit Aufwärmen, den Trainingsspielen und einem Abschluss, der alle Ergebnisse speichert. Verpasste Einheiten holst du einfach nach.</p>
                  <ul className="feature-points">
                    <li>Aufwärm-Routine, überspringbar und ohne Wertung</li>
                    <li>Trainingsspiele passend zu Fokus, Level &amp; Woche</li>
                    <li>Ergebnisse fließen automatisch in deine Statistik</li>
                  </ul>
                </div>
                <div className="feature-media">
                  <div className="mock">
                    <div className="mock-head">
                      <div><div className="t">Mein Wochenplan · Beispiel</div><div className="h">Diese Woche</div></div>
                      <div className="mock-ic"><Calendar size={18} /></div>
                    </div>
                    <div className="week">
                      <div className="day"><span className="dl">Mo</span><span className="pill g">Doppel</span></div>
                      <div className="day"><span className="dl">Di</span><span className="rest">Ruhe</span></div>
                      <div className="day today"><span className="dl">Mi</span><span className="pill o">Checkout</span></div>
                      <div className="day"><span className="dl">Do</span><span className="rest">Ruhe</span></div>
                      <div className="day"><span className="dl">Fr</span><span className="pill">Scoring</span></div>
                      <div className="day"><span className="dl">Sa</span><span className="rest">Ruhe</span></div>
                      <div className="day"><span className="dl">So</span><span className="rest">Ruhe</span></div>
                    </div>
                    <div className="flow" style={{ marginTop: 16 }}>
                      <div className="fstep skip"><div className="fn">1</div><div><div className="ft">Aufwärmen</div><div className="fd">Vierphasige Routine · überspringbar</div></div></div>
                      <div className="fstep"><div className="fn">2</div><div><div className="ft">Spiel-Session</div><div className="fd">Heute: Checkout · 40 Min</div></div></div>
                      <div className="fstep"><div className="fn">3</div><div><div className="ft">Abschluss</div><div className="fd">Ergebnisse gespeichert</div></div></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Schritt 4 · Level-System */}
          <section className="section soft">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>Schritt 4 · Level-System</span>
                <h2>Zehn Stufen, ehrlich erspielt</h2>
                <p className="lead">Dein Level kommt aus dem Performance-Test und wird am Ende jedes Plans um ±1 angepasst, je nach gewichtetem Plan-Schnitt. So bildet es deine echte Form ab, nicht einen guten Tag.</p>
              </div>
              <div className="ladder reveal in" style={{ maxWidth: 780, margin: '0 auto' }}>
                {[
                  'Einsteiger','Anfänger','Freizeitspieler','Hobbyspieler','Vereinsspieler',
                  'Solider Vereinsspieler','Ambitionierter Spieler','Wettkampfspieler','Top-Spieler','Profi-Niveau',
                ].map((t, i) => (
                  <div className={`lrow ${i === 4 ? 'cur' : ''}`} key={i}>
                    <div className="ln">{i + 1}</div>
                    <div className="lt">{t}</div>
                    {i === 4 && <div className="lc">Dein Level</div>}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Wissen Accordion */}
          <section className="section soft">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>Wissen</span>
                <h2>Mehr über Dart-Training</h2>
                <p className="lead">Tipps und Hintergründe. Klick dich durch die Themen, die dich interessieren.</p>
              </div>
              <div className="accordion reveal in">
                <details className="acc">
                  <summary>Dart Training für Anfänger<ChevronDown size={18} /></summary>
                  <div className="acc-body">
                    <p>Wer neu anfängt, spielt meistens einfach 501 und wartet darauf, dass es besser wird. Schneller geht es, wenn du Boardkontrolle, Scoring, Doppel und Checkout getrennt übst. Zwanzig fokussierte Minuten reichen dafür.</p>
                    <p>Im Guide steht ein 20-Minuten-Plan, eine Woche mit drei Trainingstagen, vier Übungen zum Nachmachen und die Zahlen, an denen du merkst, ob sich wirklich etwas tut.</p>

                    <Link className="btn btn-primary btn-sm" to={`/${language}/dart-wissen/dart-training-anfaenger`}>Zum kompletten Anfänger-Guide</Link>
                  </div>
                </details>
                <details className="acc">
                  <summary>Dart Übungen zur Verbesserung deiner Präzision<ChevronDown size={18} /></summary>
                  <div className="acc-body">
                    <p>Präzision ist der Schlüssel zu einem hohen Average. Doch Präzision kommt nicht vom bloßen Spielen, sie erfordert gezieltes, strukturiertes Training. AimUp bietet dir eine Vielzahl von Übungen, die genau darauf ausgelegt sind, deine Genauigkeit systematisch zu verbessern.</p>
                    <p>Das „Single Target Training" fokussiert sich auf ein einzelnes Feld und misst, wie oft du es innerhalb einer festgelegten Anzahl von Würfen triffst. So erkennst du schnell, welche Bereiche des Boards dir leicht fallen und wo noch Nachholbedarf besteht.</p>
                    <p>Für fortgeschrittene Spieler bietet AimUp spezielle Double- und Triple-Übungen. Das „Double Spezial" Training ist besonders wertvoll, da Doppelfelder bei Checkouts entscheidend sind. Hier trainierst du systematisch alle Doppelfelder und baust Routine auf.</p>
                    <p>Die App analysiert nach jeder Session deine Performance und zeigt dir deine Schwachstellen auf. So kannst du dein nächstes Training genau auf die Bereiche ausrichten, die am meisten Verbesserungspotenzial haben. Datengestütztes Training statt blindem Üben.</p>
                    <p>Kombiniere verschiedene Übungstypen in einer Session, um Abwechslung zu schaffen und verschiedene Fähigkeiten gleichzeitig zu trainieren. AimUps Coach-Modus stellt dir automatisch optimierte Trainingspläne zusammen.</p>
                  </div>
                </details>
                <details className="acc">
                  <summary>Warum strukturiertes Dart Training wichtig ist<ChevronDown size={18} /></summary>
                  <div className="acc-body">
                    <p>Viele Dartspieler werfen regelmäßig, verbessern sich aber kaum. Der Grund: Ohne Struktur fehlt die gezielte Arbeit an Schwächen. Wer immer nur 501 spielt, trainiert zwar sein Scoring, vernachlässigt aber Checkouts, Doubles und mentale Stärke.</p>
                    <p>Strukturiertes Training bedeutet, jede Session mit einem klaren Ziel zu beginnen. Heute arbeitest du an deinen Doubles, morgen an deinem Scoring im oberen Bereich des Boards. AimUp plant diese Abwechslung für dich und stellt sicher, dass kein Bereich zu kurz kommt.</p>
                    <p>Die Datenanalyse spielt dabei eine zentrale Rolle. Nur wer seine Trefferquoten kennt, kann gezielt an Verbesserungen arbeiten. AimUp speichert jede Session, vergleicht deine Performance über Wochen und Monate und zeigt dir messbare Fortschritte, oder eben Bereiche, die mehr Aufmerksamkeit brauchen.</p>
                    <p>Motivation ist der Schlüssel zur Konstanz. Das integrierte Level-System, die Liga und Challenges sorgen dafür, dass Training sich wie ein Spiel anfühlt. So bleibst du langfristig am Ball und siehst kontinuierlich Fortschritte.</p>
                  </div>
                </details>
              </div>
              <p className="center" style={{ marginTop: 22, fontSize: '.9rem' }}>
                <Link to={`/${language}/dart-wissen`} style={{ color: 'var(--green-ink)', fontWeight: 600 }}>
                  Alle Inhalte im Dart Wissen
                </Link>
              </p>
            </div>
          </section>
        </div>

        {/* ═══════════════ LIGA ═══════════════ */}
        <div className="view" data-view="liga" hidden={view !== 'liga'}>
          <section className="section">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>AimUp Liga</span>
                <h2>Trainieren wie im Wettkampf</h2>
                <p className="lead">Die Liga bündelt alle Trainingsspiele in einem Fortschritts- und Wettkampfrahmen: Stufe für Stufe schaltest du neue Spiele frei, sammelst für jede Session Erfahrungspunkte (EP) und vergleichst dich in der globalen Rangliste.</p>
              </div>
              <div className="quick reveal in">
                <span className="q"><Zap size={15} />Aufwärmen</span>
                <span className="q"><PlayCircle size={15} />Weiterspielen</span>
                <span className="q"><Star size={15} />Werde besser</span>
              </div>
              <p className="center lead reveal in" style={{ fontSize: '.86rem', marginTop: 14 }}>Diese drei Modi sind jederzeit verfügbar, nie gesperrt.</p>
            </div>
          </section>

          <section className="section soft">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>Die Liga-Struktur</span>
                <h2>Sieben Stufen bis zur Elite</h2>
                <p className="lead">Von „Newbie" bis „BDC, Best Dart Club": jede Stufe hat eine eigene Farbe, ein eigenes Ziel und mehr Spiele als die vorige, von 2 bis 14.</p>
              </div>
              <div className="levels reveal in">
                {[
                  { c: '#10B981', w: '#E6F8F0', t: 'Newbie',           d: 'Grundlagen & erste Schritte',   n: 2 },
                  { c: '#EF4444', w: '#FDECEC', t: 'Bloody Rookie',    d: 'Härtere Herausforderungen',     n: 4 },
                  { c: '#3B82F6', w: '#E7F0FE', t: 'Anfänger',         d: 'Technik verbessern',            n: 6 },
                  { c: '#F59E0B', w: '#FEF3DE', t: 'Fortgeschritten',  d: 'Präzision & Konsistenz',        n: 8 },
                  { c: '#6366F1', w: '#E8EAFE', t: 'Halbprofi',        d: 'Auf dem Weg zur Meisterschaft', n: 10 },
                  { c: '#8B5CF6', w: '#EFE9FE', t: 'Profi',            d: 'Meisterschaft & Wettkampf',     n: 12 },
                  { c: '#06B6D4', w: '#DEF6FB', t: 'BDC, Best Dart Club', d: 'Elite Training',           n: 14 },
                ].map((l, i) => (
                  <div className="level" key={i} style={{ ['--lc' as any]: l.c, ['--lc-wash' as any]: l.w }}>
                    <div className="top">
                      <div className="li"><span className="num" style={{ fontSize: '1.2rem', color: l.c }}>{i + 1}</span></div>
                      <span className="stage">Stufe {i + 1}</span>
                      <div className="stufe-count">{l.n}<small>Spiele</small></div>
                    </div>
                    <h3>{l.t}</h3>
                    <p>{l.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Freischalt-System */}
          <section className="section">
            <div className="wrap">
              <div className="feature reveal in">
                <div className="feature-copy">
                  <span className="eyebrow"><span className="dot"></span>Freischalt-System</span>
                  <h2>Du spielst dich<br />nach oben frei.</h2>
                  <p className="lead">Jede Stufe öffnet sich, sobald du in der vorigen ein X01-Spiel mit dem geforderten Average abschließt. Auch innerhalb einer Stufe hängen die Spiele in einer Kette. So ist nie unklar, was als Nächstes dran ist.</p>
                  <ul className="feature-points">
                    <li>Stufen schalten über 301/501 mit Ziel-Average frei</li>
                    <li>Spiele per Abschluss, Trefferquote oder Average</li>
                    <li>Ein Fortschrittsbalken zeigt, wie weit du bist</li>
                  </ul>
                </div>
                <div className="feature-media">
                  <div className="mock">
                    <div className="mock-head">
                      <div><div className="t">Freischalten · Beispiel</div><div className="h">Deine nächsten Stufen</div></div>
                      <div className="mock-ic"><Lock size={18} /></div>
                    </div>
                    <div className="urow done">
                      <div className="uh">
                        <div className="uic" style={{ background: '#3B82F6' }}><Check size={18} /></div>
                        <div><div className="ut">Anfänger</div><div className="ucond">Technik verbessern · 6 Spiele</div></div>
                        <span className="ubadge">Freigeschaltet</span>
                      </div>
                    </div>
                    <div className="urow lock">
                      <div className="uh">
                        <div className="uic" style={{ background: '#F59E0B' }}><Lock size={18} /></div>
                        <div><div className="ut">Fortgeschritten</div><div className="ucond">501 in Anfänger mit AVG ≥ 40 abschließen</div></div>
                        <span className="ubadge">Gesperrt</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.7rem', color: '#9aa1ab', marginTop: 10 }}><span>Dein Bestwert</span><span>AVG 36 / 40</span></div>
                      <div className="prog"><div className="pf" style={{ width: '90%' }}></div></div>
                    </div>
                    <div className="urow lock">
                      <div className="uh">
                        <div className="uic" style={{ background: '#6366F1' }}><Lock size={18} /></div>
                        <div><div className="ut">Halbprofi</div><div className="ucond">501 in Fortgeschritten mit AVG ≥ 50 abschließen</div></div>
                        <span className="ubadge">Gesperrt</span>
                      </div>
                      <div className="prog"><div className="pf" style={{ width: '12%' }}></div></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Spielarten */}
          <section className="section soft">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>Trainingsspiele</span>
                <h2>Spiele für jede Fähigkeit</h2>
                <p className="lead">Jedes Spiel trainiert eine konkrete Fähigkeit: Scoring, Doppel, Triple, Checkout, Bull oder Präzision. Und mit jeder Stufe wird es anspruchsvoller.</p>
              </div>
              <div className="modes-grid reveal in">
                {[
                  { ic: <Crosshair size={22} />, t: 'Around the Clock', p: 'Allround-Genauigkeit über alle Felder, der Reihe nach.' },
                  { ic: <Target size={22} />,    t: '301 & 501',        p: 'Klassische Finish-Spiele für Average und Checkout.' },
                  { ic: <BarChart2 size={22} />, t: 'Segmenttraining',  p: 'Treffsicherheit auf den Kernfeldern 20, 19 und 18.' },
                  { ic: <Target size={22} />,    t: 'Bull-Training',    p: 'Sicherheit auf Bull und Bullseye entwickeln.' },
                  { ic: <Trophy size={22} />,    t: 'Big Fish',         p: 'Das Höchst-Finish: T20, T20, Bull.' },
                  { ic: <Activity size={22} />,  t: 'Big Number',       p: 'Maximales Scoring: 180 oder 171 mit drei Pfeilen.' },
                  { ic: <Star size={22} />,      t: 'Casino',           p: 'Alle Felder in zufälliger Reihenfolge, jedes nur einmal.' },
                ].map((m, i) => (
                  <div className="mode" key={i}><div className="mi">{m.ic}</div><h3>{m.t}</h3><p>{m.p}</p></div>
                ))}
                <div className="mode more">
                  <div className="big num">+10</div>
                  <h3 style={{ marginTop: 12 }}>und viele mehr</h3>
                  <p>Doppel- &amp; Triple-Training, Perfekt Leg, Game 50, Meister des Finish und weitere warten in der App.</p>
                </div>
              </div>
            </div>
          </section>

          {/* EP & Rangliste */}
          <section className="section">
            <div className="wrap">
              <div className="feature reverse reveal in">
                <div className="feature-copy">
                  <span className="eyebrow"><span className="dot"></span>EP &amp; Rangliste</span>
                  <h2>Jede Session zählt.</h2>
                  <p className="lead">Für jede abgeschlossene Session gibt es Erfahrungspunkte, und je höher deine Trefferquote, desto mehr. Qualität wird belohnt, nicht bloß Teilnahme. Deine EP fließen in eine globale Rangliste, filterbar nach Gesamt, Monat oder Jahr und live aktualisiert.</p>
                  <div className="ep-list">
                    <div className="ep"><span className="en">Aufwärmen</span><span className="ev">20 EP</span></div>
                    <div className="ep"><span className="en">Werde besser</span><span className="ev">50 + Quote</span></div>
                    <div className="ep"><span className="en">Segmenttraining</span><span className="ev">100 + Quoten</span></div>
                    <div className="ep"><span className="en">Alle übrigen Spiele</span><span className="ev">100 + Quote</span></div>
                  </div>
                </div>
                <div className="feature-media">
                  <div className="mock">
                    <div className="lb-tag">
                      <span className="wk">Rangliste · Gesamt</span>
                      <div className="mock-ic" style={{ width: 34, height: 34 }}><Trophy size={16} /></div>
                    </div>
                    {[
                      { r: 1, c: '#C8870F', av: 'TB', avc: '#06B6D4', nm: 'Thunderbirdie', bc: '#DEF6FB', bf: '#0E7490', b: 'BDC',           pts: '12.480' },
                      { r: 2, c: '#8a8f98', av: 'BK', avc: '#00A87C', nm: 'Bierkules',     bc: '#EFE9FE', bf: '#6D28D9', b: 'Profi',         pts: '11.920', me: true, move: 'up', mn: 1, extra: 'EP · DU' },
                      { r: 3, c: '#b07d4a', av: 'DT', avc: '#6366F1', nm: 'DoubleTrouble', bc: '#E8EAFE', bf: '#4338CA', b: 'Halbprofi',     pts: '9.870',  move: 'down', mn: 1 },
                      { r: 4, c: '#aeb4bd', av: 'CK', avc: '#F59E0B', nm: 'CheckoutKing',  bc: '#FEF3DE', bf: '#B45309', b: 'Fortgeschritten', pts: '7.540' },
                      { r: 5, c: '#aeb4bd', av: 'SA', avc: '#3B82F6', nm: 'SteelTip_Anna', bc: '#FEF3DE', bf: '#B45309', b: 'Fortgeschritten', pts: '6.210', move: 'up', mn: 2 },
                    ].map((row, i) => (
                      <div key={i} className={`lb-row ${row.me ? 'me' : ''}`}>
                        <div className="rank" style={{ color: row.c }}>{row.r}</div>
                        <div className="av" style={{ background: row.avc }}>{row.av}</div>
                        <div>
                          <div className="nm">{row.nm}{row.move && <span className={`move ${row.move}`}>{row.move === 'up' ? '▲' : '▼'} {row.mn}</span>}</div>
                          <span className="badge" style={{ background: row.bc, color: row.bf }}>{row.b}</span>
                        </div>
                        <div className="pts">{row.pts}<small>{row.extra || 'EP'}</small></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* ═══════════════ PREISE ═══════════════ */}
        <div className="view" data-view="preise" hidden={view !== 'preise'}>
          <section className="section">
            <div className="wrap">
              <div className="section-head center reveal in">
                <span className="eyebrow"><span className="dot"></span>Preise</span>
                <h2>Kostenlos testen, dann entscheiden</h2>
                <p className="lead">
                  Starte mit einer kostenlosen 14-Tage-Testphase. Keine Zahlungsdaten nötig.
                  Danach entscheidest du selbst, ob du weitermachst.
                </p>
              </div>

              <div className="coach-note reveal in" style={{ maxWidth: 760, marginInline: 'auto', marginTop: 20 }}>
                <Info size={20} />
                <span>14 Tage kostenlos testen, ohne Angabe von Zahlungsdaten. Die Testphase endet automatisch, es wird nichts abgebucht.</span>
              </div>

              <div className="price-grid reveal in">
                <div className="price-card">
                  <div className="pc-head">
                    <h3>Monatlich</h3>
                  </div>
                  <div className="pc-price">
                    <span className="pc-amount num">4,45&nbsp;€</span>
                    <span className="pc-per">/ Monat</span>
                  </div>
                  <div className="pc-sub">Monatlich abgebucht, jederzeit zum Monatsende kündbar.</div>
                </div>

                <div className="price-card featured">
                  <div className="pc-head">
                    <h3>Jährlich</h3>
                    <span className="pc-badge">17 % sparen</span>
                  </div>
                  <div className="pc-price">
                    <span className="pc-amount num">44,50&nbsp;€</span>
                    <span className="pc-per">/ Jahr</span>
                  </div>
                  <div className="pc-note">entspricht 3,71&nbsp;€ pro Monat</div>
                  <div className="pc-sub">Einmal jährlich abgebucht (44,50&nbsp;€), jederzeit zum Jahresende kündbar.</div>
                </div>
              </div>

              <div className="price-features reveal in">
                <h3>In AimUp Pro enthalten</h3>
                <ul className="feature-points">
                  <li>Persönliches Training (Performance-Test, Trainingsplan, Coach-Sessions)</li>
                  <li>AimUp Liga ab Stufe Anfänger aufwärts</li>
                  <li>Das lokale Spiel Jäger &amp; Fuchs</li>
                </ul>
                <div className="price-free">
                  <div className="pf-title">Dauerhaft gratis</div>
                  <ul className="feature-points muted">
                    <li>Freundesliste und Statistik</li>
                    <li>In der Liga die Stufen Newbie und Bloody Rookie</li>
                  </ul>
                </div>
              </div>

              <div className="price-legal reveal in">
                <p>Alle Preise sind Endpreise. Als Kleinunternehmer gemäß § 6 Abs 1 Z 27 UStG weisen wir keine Umsatzsteuer aus.</p>
                <p>Das Abo verlängert sich automatisch um die gewählte Laufzeit und ist jederzeit zum Ende des laufenden Zeitraums kündbar.</p>
              </div>

              <div className="center reveal in" style={{ marginTop: 'clamp(28px,4vw,40px)' }}>
                <Link className="btn btn-primary" to={APP_REGISTER}>Jetzt kostenlos starten</Link>
              </div>
            </div>
          </section>
        </div>

        {/* ═══════════════ CTA ═══════════════ */}
        <section className="cta">
          <div className="cta-photo" aria-hidden="true">
            <img
              src={ctaPhoto}
              srcSet={`${ctaPhoto800} 800w, ${ctaPhoto1200} 1200w, ${ctaPhoto} 1600w`}
              sizes="(max-width: 900px) 100vw, 56vw"
              width={1600}
              height={1233}
              alt=""
              loading="lazy"
              decoding="async"
            />

          </div>
          <div className="wrap cta-grid">
            <div className="cta-copy">
              <span className="eyebrow"><span className="dot"></span>Dein persönlicher Dart-Coach</span>
              <h2>Bereit für deinen<br /><span className="hl">nächsten 180er?</span></h2>
              <p className="lead">Starte jetzt mit deinem persönlichen Dart-Coach und bringe dein Spiel auf das nächste Level.</p>
              <div className="cta-actions">
                <Link className="btn btn-primary" to={APP_REGISTER}>Jetzt kostenlos trainieren</Link>
              </div>
              <div className="cta-feats">
                <div className="cta-feat">
                  <span className="ic"><Target size={18} /></span>
                  <div className="t">Persönlicher Dart-Coach</div>
                  <div className="s">Individuelles Training für dich</div>
                </div>
                <div className="cta-feat">
                  <span className="ic"><BarChart2 size={18} /></span>
                  <div className="t">Ehrliche Statistiken</div>
                  <div className="s">Verstehe deine Stärken und Schwächen</div>
                </div>
                <div className="cta-feat">
                  <span className="ic"><Trophy size={18} /></span>
                  <div className="t">AimUp Liga</div>
                  <div className="s">Tritt an, sammle Punkte und steig auf</div>
                </div>
              </div>
            </div>
          </div>
          <div className="cta-card">
            <div className="num">180</div>
            <div className="ch">Mehr als nur<br />ein Wurf.</div>
            <span className="rule"></span>
            <p>Jeder große Erfolg beginnt mit einem einzigen Training.</p>
          </div>
        </section>
      </main>

      {/* ─────────────────── FOOTER ─────────────────── */}
      <PublicFooter lang={language} brandHref="#start" />
    </div>
  );
};

export default Landing;
